import { useState, useMemo, useCallback } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { PlanCategory } from '../../types/trainingTypes';
import '../../css/BulkAssignCategoryModal.css';

interface BulkAssignCategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedTopicIds: string[];
}

/**
 * BulkAssignCategoryModal — Assign multiple topics to categories at once.
 * Categories are grouped by plan with expandable sections showing plan status.
 * Additive assignment: does not remove existing category assignments.
 *
 * Validates: Requirements 4.1, 4.2, 4.3, 4.4, 4.5
 */
export const BulkAssignCategoryModal = ({
    isOpen,
    onClose,
    selectedTopicIds,
}: BulkAssignCategoryModalProps) => {
    const { plans, topics, updateTopic } = useSafetyTrainingContext();

    const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
    const [expandedPlanIds, setExpandedPlanIds] = useState<Set<string>>(new Set());
    const [isApplying, setIsApplying] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Parse categories grouped by plan
    const categoriesByPlan = useMemo(() => {
        const result: { plan: { id: string; name: string; status: string }; categories: PlanCategory[] }[] = [];
        plans.forEach(plan => {
            try {
                const parsed: PlanCategory[] = JSON.parse(plan.categories);
                if (parsed.length > 0) {
                    result.push({
                        plan: { id: plan.id, name: plan.name, status: plan.status },
                        categories: parsed,
                    });
                }
            } catch { /* ignore */ }
        });
        return result;
    }, [plans]);

    const toggleCategory = useCallback((catId: string) => {
        setSelectedCategoryIds(prev => {
            const next = new Set(prev);
            if (next.has(catId)) next.delete(catId);
            else next.add(catId);
            return next;
        });
    }, []);

    const togglePlanExpanded = useCallback((planId: string) => {
        setExpandedPlanIds(prev => {
            const next = new Set(prev);
            if (next.has(planId)) next.delete(planId);
            else next.add(planId);
            return next;
        });
    }, []);

    const handleApply = useCallback(async () => {
        if (selectedCategoryIds.size === 0) return;
        setIsApplying(true);
        setError(null);

        try {
            const newCatIds = Array.from(selectedCategoryIds);

            // Derive planIds from selected categories
            const derivedPlanIds = new Set<string>();
            categoriesByPlan.forEach(({ plan, categories: planCats }) => {
                if (planCats.some(c => selectedCategoryIds.has(c.id))) {
                    derivedPlanIds.add(plan.id);
                }
            });

            for (const topicId of selectedTopicIds) {
                const topic = topics.find(t => t.id === topicId);
                if (!topic) continue;

                // Merge: union of existing + new categories
                const existingCatIds = topic.categoryIds || [];
                const mergedCats = Array.from(new Set([...existingCatIds, ...newCatIds]));

                // Merge: union of existing + new planIds
                const existingPlanIds = topic.planIds || [];
                const mergedPlans = Array.from(new Set([...existingPlanIds, ...Array.from(derivedPlanIds)]));

                await updateTopic({
                    id: topic.id,
                    categoryIds: mergedCats,
                    planIds: mergedPlans,
                });
            }

            // Reset and close
            setSelectedCategoryIds(new Set());
            setExpandedPlanIds(new Set());
            onClose();
        } catch (err: any) {
            console.error('Failed to apply categories:', err);
            setError(err?.message || 'Failed to apply categories. Please try again.');
        } finally {
            setIsApplying(false);
        }
    }, [selectedCategoryIds, selectedTopicIds, topics, updateTopic, onClose, categoriesByPlan]);

    const handleClose = useCallback(() => {
        setSelectedCategoryIds(new Set());
        setExpandedPlanIds(new Set());
        setError(null);
        onClose();
    }, [onClose]);

    if (!isOpen) return null;

    return (
        <div className="bacm-overlay" onClick={handleClose}>
            <div className="bacm-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="bacm-header">
                    <h3>Assign to Categories</h3>
                    <button className="bacm-close-btn" onClick={handleClose} aria-label="Close">
                        ×
                    </button>
                </div>

                {/* Body */}
                <div className="bacm-body">
                    {error && (
                        <div className="bacm-error-banner">{error}</div>
                    )}

                    <p className="bacm-instruction">
                        Assign {selectedTopicIds.length} selected topic{selectedTopicIds.length !== 1 ? 's' : ''} to
                        one or more categories. A topic can be assigned to multiple categories and will count toward the time requirement of each.
                    </p>

                    {categoriesByPlan.length > 0 ? (
                        <div className="bacm-category-list">
                            {categoriesByPlan.map(({ plan, categories }) => {
                                const isExpanded = expandedPlanIds.has(plan.id);
                                const selectedInPlan = categories.filter(c => selectedCategoryIds.has(c.id)).length;
                                return (
                                    <div key={plan.id} className="bacm-plan-group">
                                        <div
                                            className="bacm-plan-group-header"
                                            onClick={() => togglePlanExpanded(plan.id)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    togglePlanExpanded(plan.id);
                                                }
                                            }}
                                        >
                                            <span className="bacm-plan-arrow">{isExpanded ? '▼' : '▶'}</span>
                                            <span className="bacm-plan-name">{plan.name}</span>
                                            <span className={`bacm-plan-status ${plan.status.toLowerCase()}`}>
                                                {plan.status}
                                            </span>
                                            {selectedInPlan > 0 && (
                                                <span className="bacm-plan-count">{selectedInPlan} selected</span>
                                            )}
                                        </div>
                                        {isExpanded && (
                                            <div className="bacm-plan-categories">
                                                {categories.map(cat => (
                                                    <label key={cat.id} className="bacm-category-item">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCategoryIds.has(cat.id)}
                                                            onChange={() => toggleCategory(cat.id)}
                                                        />
                                                        <span className="bacm-category-name">{cat.name}</span>
                                                        <span className="bacm-category-target">
                                                            {cat.targetMinutes} min target
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bacm-no-categories">
                            No categories available. Create a plan with categories first.
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bacm-footer">
                    <button className="bacm-btn-cancel" onClick={handleClose} disabled={isApplying}>
                        Cancel
                    </button>
                    <button
                        className="bacm-btn-apply"
                        onClick={handleApply}
                        disabled={selectedCategoryIds.size === 0 || isApplying}
                    >
                        {isApplying ? 'Applying...' : 'Apply to Selected'}
                    </button>
                </div>
            </div>
        </div>
    );
};
